// src/pages/billing/BillingSettingsPage.tsx
// Admin config, tabbed: billing policies (default payment terms, tax rate,
// invoice behavior per scope entity) and invoice numbering schemes — rare/
// setup-time tenant-wide config, same grouping as payment's settings tabs
// (src/pages/payment/PaymentSettingsPage.tsx). Both RPC groups check
// billing_admin/billing_viewer on the tenant, not per-BillingAccount, so this
// is platform/tenant settings rather than something on any one billing account.

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { observer } from '@react';
import { useI18n } from '@react/hooks';
import { cn } from '@core/ui';
import { BillingPoliciesTab } from '@/features/billing/BillingPoliciesTab';
import { InvoiceNumberingTab } from '@/features/billing/InvoiceNumberingTab';

type Tab = 'policies' | 'numbering';

export const BillingSettingsPage = observer(() => {
  const { t } = useI18n();
  const [tab, setTab] = useState<Tab>('policies');

  const tabs: { id: Tab; label: string }[] = [
    { id: 'policies', label: t('billing.policies.title') },
    { id: 'numbering', label: t('billing.numbering.title') },
  ];

  return (
    <div className="space-y-6">
      {/* The tab already names this page; the heading stays for screen readers. */}
      <div>
        <h1 className="sr-only">{t('billing.settings.title')}</h1>
        <nav className="text-sm">
          <Link to="/billing" className="text-muted-foreground hover:text-foreground">
            {t('billing.title')}
          </Link>
          <span className="mx-2 text-muted-foreground">/</span>
          <span className="text-foreground">{t('billing.settings.title')}</span>
        </nav>
      </div>

      <div className="flex items-center gap-1 border-b border-border" role="tablist">
        {tabs.map((tb) => (
          <button
            key={tb.id}
            type="button"
            role="tab"
            aria-selected={tab === tb.id}
            onClick={() => setTab(tb.id)}
            className={cn(
              'px-3 py-2 text-sm font-medium border-b-2 -mb-px transition-colors',
              tab === tb.id
                ? 'border-primary text-primary'
                : 'border-transparent text-muted-foreground hover:text-foreground',
            )}
          >
            {tb.label}
          </button>
        ))}
      </div>

      <div>
        {tab === 'policies' && <BillingPoliciesTab />}
        {tab === 'numbering' && <InvoiceNumberingTab />}
      </div>
    </div>
  );
});

export default BillingSettingsPage;
